/**
 * Step 5: Stackability Rules
 * 
 * Define how this promo interacts with other discounts
 * - Stackability Rules: Checkboxes for allow with referral, allow with other promos, exclusive
 * - Max Total Discount % Cap: Input field
 * - Funding: Dropdown for funded by (PLATFORM/SHARED)
 */

import React, { useRef } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { PromoFormData, PromoValidationErrors } from '../../../../../api/promos/types';
import '../../../Campaigns/CreateCampaign/components/StepperButtons.css';
import '../../../Campaigns/CreateCampaign/components/StepperFormStyles.css';
import './StepStackability.css';

interface StepStackabilityProps {
  formData: PromoFormData;
  validationErrors: PromoValidationErrors;
  onChange: (updates: Partial<PromoFormData>) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const StepStackability: React.FC<StepStackabilityProps> = ({
  formData,
  validationErrors,
  onChange,
  onNext,
  onPrevious,
}) => {
  // Ref to track input value and prevent browser autocomplete changes
  const maxDiscountRef = useRef<string>(String(formData.stackingRules?.maxTotalDiscountPercent || ''));

  // Update ref when formData changes
  React.useEffect(() => {
    maxDiscountRef.current = String(formData.stackingRules?.maxTotalDiscountPercent || '');
  }, [formData.stackingRules?.maxTotalDiscountPercent]);

  // Funding options
  const fundingOptions = [
    { value: 'PLATFORM', label: 'Platform' },
    { value: 'SHARED', label: 'Shared', isDisabled: true },
  ];

  // React-select styles (matching filter dropdowns)
  const getSelectStyles = () => ({
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    control: (base: any, state: any) => ({
      ...base,
      height: '38px',
      minHeight: '38px',
      border: '1px solid #e3e6f0',
      borderRadius: '6px',
      fontSize: '14px',
      boxShadow: 'none',
      width: '100%',
      borderColor: '#e3e6f0',
      '&:hover': {
        borderColor: '#e3e6f0'
      },
      opacity: state.isDisabled ? 0.6 : 1,
    }),
    valueContainer: (base: any) => ({
      ...base,
      height: '36px',
      padding: '0 8px',
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: '36px',
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: '0 8px',
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isDisabled ? '#f5f5f5' : state.isSelected ? '#006EFF' : state.isFocused ? '#f0f0f0' : 'white',
      color: state.isDisabled ? '#999' : state.isSelected ? 'white' : '#333',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    }),
  });

  // Handle stacking rules change - Only exclusive can be changed, others always false
  const handleStackingRuleChange = (field: string, value: boolean) => {
    onChange({
      stackingRules: {
        allowWithReferral: false, // Always false
        allowWithOtherPromos: false, // Always false
        exclusive: value,
        maxTotalDiscountPercent: formData.stackingRules?.maxTotalDiscountPercent,
      }
    });
  };

  // Handle max total discount percent change
  const handleMaxDiscountChange = (value: number | undefined) => {
    onChange({
      stackingRules: {
        allowWithReferral: false, // Always false
        allowWithOtherPromos: false, // Always false
        exclusive: formData.stackingRules?.exclusive || false,
        maxTotalDiscountPercent: value,
      }
    });
  };

  // Handle funding change
  const handleFundingChange = (selectedOption: any) => {
    if (selectedOption && !selectedOption.isDisabled) {
      onChange({
        funding: {
          fundedBy: selectedOption.value,
          sharePercentage: selectedOption.value === 'PLATFORM' ? 100 : formData.funding?.sharePercentage || 100,
        }
      });
    }
  };

  return (
    <form autoComplete="off" noValidate>
      <div className="mb-4">
        <h5 className="mb-2">Stackability Rules</h5>
        <p className="text-muted mb-0">Define how this promo interacts with other discounts</p>
      </div>

      <Row>
        {/* Stackability Rules Section - No border, with bottom border */}
        <Col lg={12} className="mb-4">
          <div className="stackability-section-no-border">
            
            {/* Checkboxes - Only Exclusive option is enabled */}
            <Row>
              <Col lg={12} className="mb-3">
                <div className="stackability-checkbox-card disabled">
                  <Form.Check
                    type="checkbox"
                    id="allowWithReferral"
                    label={
                      <div>
                        <div className="stackability-checkbox-label">Allow with Referral Discount</div>
                        <div className="stackability-checkbox-description">Can be combined with referral codes</div>
                      </div>
                    }
                    checked={false}
                    disabled={true}
                    className="stackability-checkbox"
                  />
                </div>
              </Col>
              <Col lg={12} className="mb-3">
                <div className="stackability-checkbox-card disabled">
                  <Form.Check
                    type="checkbox"
                    id="allowWithOtherPromos"
                    label={
                      <div>
                        <div className="stackability-checkbox-label">Allow with Other Promos</div>
                        <div className="stackability-checkbox-description">Can stack with other promotional discounts</div>
                      </div>
                    }
                    checked={false}
                    disabled={true}
                    className="stackability-checkbox"
                  />
                </div>
              </Col>
              <Col lg={12} className="mb-3">
                <div className="stackability-checkbox-card">
                  <Form.Check
                    type="checkbox"
                    id="exclusive"
                    label={
                      <div>
                        <div className="stackability-checkbox-label">Exclusive Discount</div>
                        <div className="stackability-checkbox-description">Cannot be combined with any other discounts</div>
                      </div>
                    }
                    checked={formData.stackingRules?.exclusive || false}
                    onChange={(e) => handleStackingRuleChange('exclusive', e.target.checked)}
                    className="stackability-checkbox"
                  />
                </div>
              </Col>
            </Row>

            {/* Max Total Discount % Cap */}
            <Row className="mt-3">
              <Col lg={6}>
                <Form.Group className="stepper-form-group">
                  <Form.Label className="stepper-form-label">
                    Max Total Discount % Cap <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    className={`stepper-form-control ${validationErrors.stackingRules?.maxTotalDiscountPercent ? 'is-invalid' : ''}`}
                    placeholder="Enter max total discount percentage"
                    value={formData.stackingRules?.maxTotalDiscountPercent || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      maxDiscountRef.current = val;
                      handleMaxDiscountChange(val ? Number(val) : undefined);
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      maxDiscountRef.current = val;
                      if (val !== String(formData.stackingRules?.maxTotalDiscountPercent || '')) {
                        handleMaxDiscountChange(val ? Number(val) : undefined);
                      }
                    }}
                    onWheel={(e) => {
                      // Prevent scroll from changing number input values
                      e.currentTarget.blur();
                    }}
                    min="0"
                    max="100"
                    step="0.01"
                    required
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    inputMode="numeric"
                    data-lpignore="true"
                    data-form-type="other"
                  />
                  {validationErrors.stackingRules?.maxTotalDiscountPercent && (
                    <div className="stepper-invalid-feedback">
                      {validationErrors.stackingRules.maxTotalDiscountPercent}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Col>

        {/* Funding Section - No border */}
        <Col lg={12} className="mb-4">
          <div className="stackability-section-funding">
            <h6 className="stackability-section-title mb-3">Funding</h6>
            <p className="stackability-section-subtitle mb-3">Define who bears the cost of this discount</p>
            <Row>
              <Col lg={6}>
                <Form.Group className="stepper-form-group">
                  <Form.Label className="stepper-form-label">Funded By</Form.Label>
                  <Select
                    styles={getSelectStyles()}
                    options={fundingOptions}
                    value={fundingOptions.find(opt => opt.value === (formData.funding?.fundedBy || 'PLATFORM')) || null}
                    onChange={handleFundingChange}
                    placeholder="Select Funding"
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    isOptionDisabled={(option: any) => option.isDisabled}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      <div className="d-flex justify-content-between mt-4">
        <button className="stepper-back-button" onClick={onPrevious}>
          <i className="ph-arrow-left"></i>
          Back
        </button>
        <button 
          className="stepper-next-button" 
          onClick={onNext}
          disabled={!formData.stackingRules?.maxTotalDiscountPercent || formData.stackingRules.maxTotalDiscountPercent <= 0}
        >
          Next
          <i className="ph-arrow-right"></i>
        </button>
      </div>
    </form>
  );
};

export default StepStackability;
